/// <reference types="vite/client" />
import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { z } from 'zod';
import { zodTextFormat } from 'openai/helpers/zod';
import { getJudgingDataForCategory } from 'services/vistaJudging';
import { PPTTranscriptionState } from '../types';

import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Set once at module load so the worker is ready before any getDocument() call
import('pdfjs-dist').then(lib => {
  lib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
});

const API_BASE_URL =
  import.meta.env.VITE_CMS_BASEURL || 'https://content.srx.id';
const CMS_API_TOKEN = import.meta.env.VITE_CMS_API_TOKEN;

const CriteriaExtractionSchema = z.object({
  criteria_extracts: z
    .array(
      z.object({
        criteria_name: z.string().describe('The name of the criteria/aspect'),
        key_points: z
          .array(z.string())
          .length(3)
          .describe('Exactly 3 key bullet points that address this criteria'),
      })
    )
    .length(3)
    .describe('Exactly 3 extracts, one for each criteria/aspect'),
});

const currentYear = new Date().getFullYear();

async function parsePPTX(buffer: ArrayBuffer): Promise<string> {
  const { default: JSZip } = await import('jszip');
  const zip = await JSZip.loadAsync(buffer);

  const slideFiles = Object.keys(zip.files)
    .filter(name => /^ppt\/slides\/slide\d+\.xml$/.test(name))
    .sort((a, b) => {
      const numA = parseInt(a.match(/\d+/)![0]);
      const numB = parseInt(b.match(/\d+/)![0]);
      return numA - numB;
    });

  const slides: string[] = [];
  for (let i = 0; i < slideFiles.length; i++) {
    const xml = await zip.files[slideFiles[i]].async('string');
    const doc = new DOMParser().parseFromString(xml, 'text/xml');
    const textNodes = doc.getElementsByTagNameNS(
      'http://schemas.openxmlformats.org/drawingml/2006/main',
      't'
    );
    const texts: string[] = [];
    for (let j = 0; j < textNodes.length; j++) {
      const text = textNodes[j].textContent?.trim();
      if (text) texts.push(text);
    }
    slides.push(`--- Slide ${i + 1} ---\n\n${texts.join('\n')}`);
  }

  return slides.join('\n\n');
}

async function parsePDF(buffer: ArrayBuffer): Promise<string> {
  const pdfjsLib = await import('pdfjs-dist');

  const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(buffer) })
    .promise;
  const pages: string[] = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    const pageText = (textContent.items as Array<{ str?: string }>)
      .map(item => item.str ?? '')
      .join(' ');
    pages.push(`--- Page ${i} ---\n\n${pageText}`);
  }

  return pages.join('\n\n');
}

export async function transcribePPT(file: File): Promise<string> {
  try {
    const extension = file.name.split('.').pop()?.toLowerCase();
    const isPDF = file.type === 'application/pdf' || extension === 'pdf';
    console.log(
      '🔍 Detected file type:',
      isPDF ? 'PDF' : 'PPTX',
      'for:',
      file.name
    );

    const buffer = await file.arrayBuffer();
    const markdown_content = isPDF
      ? await parsePDF(buffer)
      : await parsePPTX(buffer);

    const contentLength = markdown_content.trim().length;
    const hasErrorMessage =
      markdown_content.includes('**Error**') ||
      markdown_content.includes('[Error extracting text');

    if (contentLength < 50 && hasErrorMessage) {
      throw new Error(
        'Document file could not be processed - it may be image-based or corrupted'
      );
    }

    if (contentLength < 20) {
      throw new Error(
        'Document file appears to contain no readable text content'
      );
    }

    console.log(
      '✅ Document processing successful, content length:',
      contentLength
    );
    return markdown_content;
  } catch (error) {
    console.error('❌ Document transcription failed:', error);
    throw error;
  }
}

export async function extractCriteriaFromPPT(
  pptText: string,
  selectedCategory: string,
  executeRecaptcha?: (action: string) => Promise<string>
): Promise<string> {
  try {
    const judgingData = await getJudgingDataForCategory(
      selectedCategory,
      currentYear
    );

    if (
      !judgingData ||
      !judgingData.aspects ||
      judgingData.aspects.length !== 3
    ) {
      throw new Error(
        `Failed to get 3 criteria for category: ${selectedCategory}. Found ${judgingData?.aspects?.length || 0} aspects.`
      );
    }

    const cleanAspectName = (fullName: string, categoryName: string) => {
      const prefixToRemove = `${categoryName} -`;
      if (fullName.startsWith(prefixToRemove)) {
        return fullName.substring(prefixToRemove.length).trim();
      }
      const parts = fullName.split(' - ');
      return parts.length > 1 ? parts.slice(1).join(' - ').trim() : fullName;
    };

    const criteria = judgingData.aspects.map(
      (aspect: {
        name: string;
        weight: number;
        description?: string;
        scale_guidance?: Record<string, string>;
      }) => ({
        name: cleanAspectName(aspect.name, selectedCategory),
        fullName: aspect.name,
        weight: aspect.weight,
        description: aspect.description || '',
        scale_guidance: aspect.scale_guidance || {},
      })
    );

    const extractionPrompt = `You are an expert analyzer. Extract and summarize information from the following presentation text that is specifically relevant to each of the 3 award criteria.

AWARD CATEGORY: ${selectedCategory}

CRITERIA TO EXTRACT FOR:
${criteria
  .map(
    (
      c: {
        name: string;
        description: string;
        scale_guidance: Record<string, string>;
      },
      i: number
    ) => `
${i + 1}. ${c.name}
   Description: ${c.description}
   ${Object.keys(c.scale_guidance).length > 0 ? `Scoring Guide: Score 1 = "${c.scale_guidance['1'] || ''}", Score 10 = "${c.scale_guidance['10'] || ''}"` : ''}
`
  )
  .join('')}

PRESENTATION TEXT TO ANALYZE:
${pptText}

INSTRUCTIONS:
- For each criteria, extract ONLY the information from the presentation text that directly relates to that specific criteria
- Present the information as exactly 3 key bullet points per criteria
- Focus on measurable outcomes, specific examples, and concrete evidence
- If insufficient information is found for a criteria, create concise summaries or note "Limited information available in presentation"
- Maintain the original factual content - do not add interpretations or assumptions

Extract information for all 3 criteria in the exact order provided above.`;

    let recaptchaToken: string | undefined;
    if (import.meta.env.VITE_DISABLE_RECAPTCHA !== 'true' && executeRecaptcha) {
      recaptchaToken = await executeRecaptcha('ppt_transcription');
    }

    const response = await fetch(`${API_BASE_URL}/api/vista-ai/responses`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${CMS_API_TOKEN}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        input: extractionPrompt,
        text: {
          format: zodTextFormat(
            CriteriaExtractionSchema,
            'criteria_extraction'
          ),
        },
        recaptchaToken: recaptchaToken || 'dev-bypass',
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Vista-AI extraction failed: ${response.statusText} - ${errorText}`
      );
    }

    const aiResponse = await response.json();
    const extractionText = aiResponse.output?.[0]?.content?.[0]?.text || '';

    try {
      const parsedExtraction = JSON.parse(extractionText);
      const validatedExtraction =
        CriteriaExtractionSchema.parse(parsedExtraction);

      const formattedExtraction = validatedExtraction.criteria_extracts
        .map(extract => {
          return `
=== ${extract.criteria_name} ===

${extract.key_points.map(point => `• ${point}`).join('\n')}
`;
        })
        .join('\n');

      return formattedExtraction;
    } catch (parseError) {
      throw new Error(
        `Vista-AI returned invalid structured response: ${(parseError as Error).message}`
      );
    }
  } catch (error) {
    console.error('❌ Criteria extraction failed:', error);
    throw error;
  }
}

export function usePPTTranscription(category: string) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [pptTranscription, setPptTranscription] =
    useState<PPTTranscriptionState>({
      isTranscribing: false,
      isCompleted: false,
      extractedData: null,
      error: null,
    });

  const startPPTTranscription = async (file: File) => {
    setPptTranscription(prev => ({
      ...prev,
      isTranscribing: true,
      isCompleted: false,
      error: null,
    }));

    try {
      const fullPPTText = await transcribePPT(file);
      const criteriaExtraction = await extractCriteriaFromPPT(
        fullPPTText,
        category,
        executeRecaptcha
      );

      setPptTranscription({
        isTranscribing: false,
        isCompleted: true,
        extractedData: criteriaExtraction,
        error: null,
      });
    } catch (error) {
      console.error('❌ PPT processing failed:', error);
      setPptTranscription({
        isTranscribing: false,
        isCompleted: false,
        extractedData: null,
        error: (error as Error).message,
      });
    }
  };

  return { pptTranscription, startPPTTranscription };
}
